import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router';

interface UserInfo {
  id: number;
  name: string;
  email: string;
  token: string;

  // agrega los campos adicionales que necesites
}

interface UserSigninState {
  userInfo: UserInfo | null;
}

interface RootState {
  userSignin: UserSigninState;
}

/**
 * @component PrivateRoute
 * @description Componente de protección de rutas para React Router v6.
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                    ¿Cómo funciona?                                                                                   │
 * │                                                                                                                           │
 * │  usuarioAutenticado ──► <Outlet />  ──► Renderiza ruta hijo                                       │
 * │  usuarioAnónimo     ──► <Navigate /> ──► Redirige a /signin                                     │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * @why_outlet
 * En React Router v6 desapareció el patrón `<Route render={...} />` de v5.
 * Ahora se usan rutas anidadas: PrivateRoute actúa como "guardián" y
 * <Outlet /> es el placeholder donde el router inyecta el componente hijo.
 * PrivateRoute NO necesita saber qué componente va a renderizar.
 *
 * @usage
 * ```tsx
 * <Route element={<PrivateRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 *   <Route path="/profile"   element={<Profile />} />
 * </Route>
 * ```
 *
 * @redux
 * Obtiene `userInfo` desde el store de Redux (slice: userSignin).
 * Si `userInfo` es null (no autenticado), redirige automáticamente.
 */

export default function PrivateRoute(): React.JSX.Element {
  const { userInfo } = useSelector((state: RootState) => state.userSignin);
  return userInfo ? <Outlet /> : <Navigate to="/signin" replace />;
}
